import { constants, domain } from '../../../common';

const { repeaterFormItemTypes, repeaterCadenceTypes } = constants;
const { classifyRepeaterEntryMode } = domain;
const { entryModes } = classifyRepeaterEntryMode;

export const DEFAULT_ADD_ENTRIES = {
   entryMode: entryModes.ADD_ENTRIES,
   idRepeaterCadence: repeaterCadenceTypes.AT_LEAST.id,
   timesToRepeat: 1
};

const numericCadenceIds = [
   repeaterCadenceTypes.EXACTLY.id,
   repeaterCadenceTypes.AT_LEAST.id,
   repeaterCadenceTypes.AT_MOST.id
];

const matchesId = (value, id) => value === id || value === String(id);

const normalizeTimesToRepeat = (value) => {
   if (Number.isInteger(value)) return value >= 1 && value <= 100 ? value : null;
   if (typeof value !== 'string') return null;

   const trimmed = value.trim();
   if (!/^\d+$/.test(trimmed)) return null;

   const parsed = Number(trimmed);
   return parsed >= 1 && parsed <= 100 ? parsed : null;
};

export const getCompatibleNumericRequirement = ({ idRepeaterCadence, timesToRepeat }) => {
   const cadence = numericCadenceIds.find((id) => matchesId(idRepeaterCadence, id));
   const count = normalizeTimesToRepeat(timesToRepeat);

   return cadence && count != null ? { idRepeaterCadence: cadence, timesToRepeat: count } : null;
};

const classifyOptions = (options = {}) =>
   classifyRepeaterEntryMode({
      idRepeaterType: options.idRepeaterType,
      entryMode: options.entryMode,
      idRepeaterCadence: options.idRepeaterCadence,
      timesToRepeat: options.timesToRepeat
   });

export const canUseFullList = (options = {}, datasourceAvailable = null) =>
   options.binding?.noDataSource != null && datasourceAvailable === true;

export const newListRepeaterOptions = () => ({
   idRepeaterType: repeaterFormItemTypes.LIST.id,
   ...DEFAULT_ADD_ENTRIES
});

export const transitionRepeaterType = (options = {}, idRepeaterType) => {
   if (matchesId(options.idRepeaterType, idRepeaterType)) return options;

   const next = { ...options, idRepeaterType };
   const numeric = getCompatibleNumericRequirement(options);

   if (idRepeaterType === repeaterFormItemTypes.LIST.id) {
      return {
         ...next,
         entryMode: entryModes.ADD_ENTRIES,
         ...(numeric || DEFAULT_ADD_ENTRIES)
      };
   }

   if (idRepeaterType === repeaterFormItemTypes.FREE_FLOW.id) {
      delete next.entryMode;
      if (numeric) {
         next.idRepeaterCadence = numeric.idRepeaterCadence;
         next.timesToRepeat = numeric.timesToRepeat;
      } else {
         next.idRepeaterCadence = repeaterCadenceTypes.ANY_NUMBER_OF.id;
         next.timesToRepeat = null;
      }
      return next;
   }

   return next;
};

export const transitionEntryMode = (options = {}, entryMode) => {
   const classification = classifyOptions(options);
   if (entryMode === classification.effectiveEntryMode) return options;

   if (entryMode === entryModes.FULL_LIST) {
      return { ...options, entryMode: entryModes.FULL_LIST, idRepeaterCadence: null, timesToRepeat: null };
   }

   if (entryMode === entryModes.ADD_ENTRIES) {
      return { ...options, ...DEFAULT_ADD_ENTRIES };
   }

   return options;
};

export const transitionAddEntriesRequirement = (options = {}, { idRepeaterCadence, timesToRepeat }) => {
   const numeric = getCompatibleNumericRequirement({ idRepeaterCadence, timesToRepeat });
   if (!numeric) return { ...options, ...DEFAULT_ADD_ENTRIES };

   const currentNumeric = getCompatibleNumericRequirement(options);
   if (
      currentNumeric &&
      currentNumeric.idRepeaterCadence === numeric.idRepeaterCadence &&
      currentNumeric.timesToRepeat === numeric.timesToRepeat
   ) {
      return options;
   }

   return {
      ...options,
      entryMode: entryModes.ADD_ENTRIES,
      idRepeaterCadence: numeric.idRepeaterCadence,
      timesToRepeat: numeric.timesToRepeat
   };
};

export const repairAsFreeFlow = (options = {}) => {
   const next = { ...options, idRepeaterType: repeaterFormItemTypes.FREE_FLOW.id };
   const numeric = getCompatibleNumericRequirement(options);
   delete next.entryMode;

   if (numeric) {
      next.idRepeaterCadence = numeric.idRepeaterCadence;
      next.timesToRepeat = numeric.timesToRepeat;
   } else {
      next.idRepeaterCadence = repeaterCadenceTypes.ANY_NUMBER_OF.id;
      next.timesToRepeat = null;
   }

   return next;
};

export const applyRepeaterTypeChange = transitionRepeaterType;
export const applyEntryModeChange = (options, entryMode) => transitionEntryMode(options, entryMode);
export const applyRequiresChange = transitionAddEntriesRequirement;
